import { Checkbox } from "@fluentui/react";
import React from "react";
import { skillsCreate, skillsUpdate } from "../../../../api/api";
import { getToolLabel } from "../../../../hooks/useAiTools";
import { useModels } from "../../../../state/api/ai/useModels";
import { useExternalSkills, useSkills } from "../../../../state/api/ai/useSkills";
import { AI_TOOL, Doc, INTERACTION_TYPE, InteractionTypeMap, Skill } from "../../../../types";
import Button from "../../buttons/Button";
import Submit from "../../buttons/Submit";
import SearchableComboBox from "../../combobox/SearchableComboBox";
import FieldWithLabel from "../../formik/FieldWithLabel";
import Icon from "../../icons/Icon";
import { Dialog, DialogBody, DialogFooter, ModalForm } from "../../modal/ModalForm";

interface Props {
  skill?: Doc<Skill>
  duplicate?: boolean
}

export default function EditSkillButton(props: Props) {

  const { skill, duplicate } = props;

  const isCopy = !!skill && !!duplicate;

  return (
    <Dialog
      dialog={<SkillEditForm {...props} />}
      trigger={(
        <Button
          variant={isCopy ? "subtle" : "solid"}
          text={isCopy ? "Duplicate" : (skill ? "Edit" : "New")}
          icon={isCopy ? "copy" : (skill ? "pen" : "plus")}
        />
      )}
    />
  )
}

function SkillEditForm(props: Props) {

  const [startsImmediatly, setStartsImmediatly] = React.useState<boolean>(!!props.skill?.firstMessage);
  const [allowStructured, setAllowStructured] = React.useState<boolean>(!!props.skill?.jsonSchema);

  const {
    loadingExternalSkills,
    externalSkills
  } = useExternalSkills();

  const {
    skills,
    reloadSkills
  } = useSkills();

  const {
    models,
    loadingModels
  } = useModels();

  const {
    skill,
    duplicate
  } = props;

  const isCopy = !!skill && !!duplicate;

  return (
    <ModalForm
      title={isCopy ? "Duplicate Skill" : (skill ? "Edit Skill" : "New Skill")}
      description={isCopy ? "Create a new skill from this one" : (skill ? "Edit this Skill" : "Create a new Skill")}
      initialValues={{
        label: skill?.label ?? "",
        model: skill?.model ?? "",
        fileInputRequired: skill?.fileInputRequired ?? false,
        modelFileUploadOnStart: skill?.modelFileUploadOnStart ?? false,
        containerOnly: skill?.containerOnly ?? false,
        singleFileUpload: skill?.singleFileUpload ?? false,
        firstMessage: skill?.firstMessage ?? "",
        firstMessageTool: skill?.firstMessageTool ?? undefined,
        description: skill?.description ?? "",
        skill_id: skill?.skill_id ?? "",
        jsonSchema: skill?.jsonSchema ?? "",
        inTesting: skill?.inTesting ?? false,
        type: skill?.type ?? INTERACTION_TYPE.REGULAR_CHAT
      } satisfies Skill}
      onSubmit={async (values, apiCall) => {
        const res = await apiCall(
          (skill && !isCopy)
            ? skillsUpdate({ ...values, _id: skill._id })
            : skillsCreate(values)
        );

        if (!res.success) {
          return false;
        }

        await reloadSkills();
        return true;
      }}
    >
      {
        formik => {

          const tb = (t: INTERACTION_TYPE) => {
            const a = t === formik.values.type;

            return (
              <Button
                variant={a ? "solid" : "subtle"}
                text={InteractionTypeMap[t]}
                onClick={() => formik.setFieldValue("type", t)}
              />
            )
          }

          const i = (l: string) => (
            <div className="d-flex flex-row align-items-center gap-1">
              <Icon icon="info-circle" size={10} />
              <span className="font-code font-xs">
                {l}
              </span>
            </div>
          )

          return (
            <>
              <DialogBody>
                <div className="card p-3 d-flex flex-column gap-2">
                  <h6 className="fw-bold">Metadata</h6>
                  <div className="d-flex flex-column">
                    <span className="form-label">Type</span>
                    <div className="d-flex flex-row gap-2">
                      {tb(INTERACTION_TYPE.ANALYSIS)}
                      {tb(INTERACTION_TYPE.DEAL_CHAT)}
                      {tb(INTERACTION_TYPE.REGULAR_CHAT)}
                      {tb(INTERACTION_TYPE.TOOL_CHAT)}
                    </div>
                  </div>
                  {
                    formik.values.type !== INTERACTION_TYPE.ANALYSIS && (
                      <>
                        <FieldWithLabel
                          label="Label / Persona"
                          placeholder="Tool name or persona for people to use"
                          name="label"
                        />
                      </>
                    )
                  }

                  <div className="d-flex flex-column w-100 gap-1">
                    <FieldWithLabel
                      as="textarea"
                      label="Description"
                      placeholder="Enter a description for people to understand this skill"
                      name="description"
                    />
                    {i("This shows in the skill list or when a chat is empty or a tool is waiting for use.")}
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <Checkbox
                      checked={formik.values.inTesting}
                      onChange={(c, v) => formik.setFieldValue("inTesting", !!v)}
                      label="In testing (admins only)"
                    />
                    {i(
                      formik.values.inTesting
                        ? "Only admins can see and use this skill. Untick to make it live for everyone."
                        : "This skill is live and visible to everyone with access."
                    )}
                  </div>
                </div>
                <div className="card p-3">
                  <h6 className="fw-bold">Behavior</h6>
                  <div className="d-flex flex-column gap-4">
                    <div className="d-flex w-100 flex-column gap-2">
                      <Checkbox
                        label="Start immediatly on use"
                        checked={startsImmediatly}
                        onChange={(e, c) => {
                          setStartsImmediatly(!!c);
                          if (!c) formik.setFieldValue("firstMessage", "");
                        }}
                      />
                      {i(
                        startsImmediatly
                          ? "The system message you set that triggers the skill will not be visible to the user in the chat history."
                          : "The user is required to write a message or upload a file to start using this tool."
                      )}
                    </div>
                    {
                      startsImmediatly
                      && (
                        <FieldWithLabel
                          as="textarea"
                          name="firstMessage"
                          label="First message on start"
                        />
                      )
                    }
                    <div className="d-flex w-100 flex-column gap-2">
                      <Checkbox
                        label="Upload the open workbook on start"
                        checked={formik.values.modelFileUploadOnStart}
                        onChange={(e, c) => formik.setFieldValue("modelFileUploadOnStart", !!c)}
                      />
                      {i(
                        formik.values.modelFileUploadOnStart
                          ? "The user's Excel model is attached to the first message as .xlsx. Outside Excel the chat still starts, just without the file."
                          : "The assistant can still pull the workbook in itself whenever it needs it."
                      )}
                    </div>
                    <div className="d-flex flex-column w-100 gap-1">
                      <SearchableComboBox
                        onChange={x => formik.setFieldValue("firstMessageTool", x)}
                        label="Force Tool use on start"
                        value={formik.values.firstMessageTool}
                        values={[
                          AI_TOOL.GET_DATASET_META,
                          AI_TOOL.WEB_SEARCH
                        ]}
                        itemToId={x => x}
                        itemToString={x => getToolLabel(x)}
                      />
                      {i(
                        "Forces the assistant to use a tool as his first action when a new chat is started."
                      )}
                    </div>
                  </div>
                </div>
                <div className="card p-3">
                  <h6 className="fw-bold">Model</h6>
                  <div className="d-flex flex-column gap-3">
                    <SearchableComboBox
                      onChange={x => formik.setFieldValue("skill_id", x?.id)}
                      label="AI Skill"
                      itemToId={x => x.id}
                      itemToString={x => x.display_title}
                      values={externalSkills ?? []}
                      value={formik.values.skill_id}
                      loading={loadingExternalSkills}
                    />
                    <div className="d-flex flex-column w-100 gap-1">
                      <SearchableComboBox
                        onChange={m => formik.setFieldValue("model", m?._id)}
                        label="Model"
                        cannotClearValue
                        highlightChecked
                        values={models}
                        value={formik.values.model}
                        itemToId={x => x._id}
                        itemToString={x => x.label || x.externalModelId}
                        loading={loadingModels}
                      />
                      {i(
                        "Models carry the Anthropic model, reasoning effort, thinking and price. Manage them under Settings > Models."
                      )}
                    </div>
                  </div>
                </div>
                <div className="card p-3">
                  <h6 className="fw-bold">Input & Output</h6>
                  <div className="d-flex flex-column gap-3">
                    {
                      formik.values.type !== INTERACTION_TYPE.ANALYSIS && (
                        <div className="d-flex flex-column gap-2">
                          <Checkbox
                            checked={formik.values.fileInputRequired}
                            onChange={(c, v) => formik.setFieldValue("fileInputRequired", v)}
                            label="File input required on use"
                          />
                          {
                            formik.values.fileInputRequired && i("Files must be selected when starting a chat")
                          }
                        </div>
                      )
                    }
                    {
                      formik.values.type !== INTERACTION_TYPE.ANALYSIS && (
                        <div className="d-flex flex-column gap-2">
                          <Checkbox
                            checked={formik.values.containerOnly}
                            onChange={(c, v) => formik.setFieldValue("containerOnly", !!v)}
                            label="Container only"
                          />
                          {i(
                            formik.values.containerOnly
                              ? "Uploaded files only go into the code execution container. The assistant reads them with code instead of getting their full text in every request."
                              : "Uploaded files go into the container and into the conversation as text, which is resent with every request."
                          )}
                        </div>
                      )
                    }
                    {
                      formik.values.type !== INTERACTION_TYPE.ANALYSIS && (
                        <div className="d-flex flex-column gap-2">
                          <Checkbox
                            checked={formik.values.singleFileUpload}
                            onChange={(c, v) => formik.setFieldValue("singleFileUpload", !!v)}
                            label="One file per chat"
                          />
                          {
                            formik.values.singleFileUpload && i("Only one file can be uploaded per chat. Attaching is blocked once a file was sent.")
                          }
                        </div>
                      )
                    }
                    <div className="d-flex flex-column gap-2">
                      <Checkbox
                        checked={allowStructured}
                        onChange={(c, v) => {
                          const isChecked = !!v;
                          setAllowStructured(isChecked);

                          if (!isChecked) {
                            formik.setFieldValue("jsonSchema", "");
                          }
                        }}
                        label="Structured output (JSON)"
                      />
                      {
                        allowStructured && (
                          <FieldWithLabel
                            as="textarea"
                            label="JSON schema"
                            name="jsonSchema"
                          />
                        )
                      }
                    </div>
                  </div>
                </div>
              </DialogBody>
              <DialogFooter>
                <Submit />
              </DialogFooter>
            </>
          )
        }
      }
    </ModalForm>
  )
}
